<template>
    <div>
        <h3 class="mt-10 t-heading-medium">Endereço</h3>
        <v-row>
            <v-col cols="4">
                <v-text-field
                    v-model="address.zip_code"
                    label="CEP"
                    counter
                    maxlength="10"
                    v-mask="'##.###-###'"
                    :error-messages="errors.list('zip_code')"
                    @blur="searchZipCode"
                    :required="!optional"
                ></v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="4">
                <v-text-field
                    v-model="address.street"
                    label="Rua"
                    counter
                    maxlength="150"
                    :error-messages="errors.list('street')"
                    @blur="errors.remove('street')"
                    :required="!optional"
                ></v-text-field>
            </v-col>
            <v-col cols="4">
                <v-text-field
                    v-model="address.number"
                    label="Número"
                    :error-messages="errors.list('number')"
                    @blur="errors.remove('number')"
                    :required="!optional"
                    ref="addressNumber"
                ></v-text-field>
            </v-col>
            <v-col cols="4">
                <v-text-field
                    v-model="address.district"
                    label="Bairro"
                    counter
                    maxlength="150"
                    :error-messages="errors.list('district')"
                    @blur="errors.remove('district')"
                    :required="!optional"
                ></v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="4">
                <v-select
                    v-model="address.state"
                    :items="stateOptions"
                    item-value="uf"
                    item-text="label"
                    label="Estado"
                    clearable
                    :error-messages="errors.list('state')"
                    @blur="errors.remove('state')"
                    @change="handleStateChange"
                    :required="!optional"
                ></v-select>
            </v-col>
            <v-col cols="4">
                <cp-select
                    v-model="address.city"
                    :config="citySelectConfig"
                    name="select-city"
                    label="Cidade"
                    item-value="name"
                    :item-text="(item) => item.name"
                    clearable
                    :disabled="!citySelectConfig.filters.state"
                    :error-messages="errors.list('city')"
                    @blur="errors.remove('city')"
                    :required="!optional"
                ></cp-select>
            </v-col>
            <v-col cols="4">
                <v-text-field
                    v-model="address.country"
                    label="País"
                    counter
                    maxlength="150"
                    :error-messages="errors.list('country')"
                    @blur="errors.remove('country')"
                    :required="!optional"
                ></v-text-field>
            </v-col>
        </v-row>
        <v-row>
            <v-col cols="4">
                <v-text-field
                    v-model="address.complement"
                    label="Complemento"
                    counter
                    maxlength="150"
                    :error-messages="errors.list('complement')"
                    @blur="errors.remove('complement')"
                    :required="!optional"
                ></v-text-field>
            </v-col>
        </v-row>
    </div>
</template>

<script>
import { mask } from "vue-the-mask"

const BRAZILIAN_STATES = [
    { uf: 'AC', label: 'Acre (AC)' }, { uf: 'AL', label: 'Alagoas (AL)' },
    { uf: 'AP', label: 'Amapá (AP)' }, { uf: 'AM', label: 'Amazonas (AM)' },
    { uf: 'BA', label: 'Bahia (BA)' }, { uf: 'CE', label: 'Ceará (CE)' },
    { uf: 'DF', label: 'Distrito Federal (DF)' }, { uf: 'ES', label: 'Espírito Santo (ES)' },
    { uf: 'GO', label: 'Goiás (GO)' }, { uf: 'MA', label: 'Maranhão (MA)' },
    { uf: 'MT', label: 'Mato Grosso (MT)' }, { uf: 'MS', label: 'Mato Grosso do Sul (MS)' },
    { uf: 'MG', label: 'Minas Gerais (MG)' }, { uf: 'PA', label: 'Pará (PA)' },
    { uf: 'PB', label: 'Paraíba (PB)' }, { uf: 'PR', label: 'Paraná (PR)' },
    { uf: 'PE', label: 'Pernambuco (PE)' }, { uf: 'PI', label: 'Piauí (PI)' },
    { uf: 'RJ', label: 'Rio de Janeiro (RJ)' }, { uf: 'RN', label: 'Rio Grande do Norte (RN)' },
    { uf: 'RS', label: 'Rio Grande do Sul (RS)' }, { uf: 'RO', label: 'Rondônia (RO)' },
    { uf: 'RR', label: 'Roraima (RR)' }, { uf: 'SC', label: 'Santa Catarina (SC)' },
    { uf: 'SP', label: 'São Paulo (SP)' }, { uf: 'SE', label: 'Sergipe (SE)' },
    { uf: 'TO', label: 'Tocantins (TO)' },
];

export default {
    props: {
        address: {
            type: Object,
            required: true
        },
        optional: {
            type: Boolean,
            default: false,
        },
    },

    directives: { mask },

    data() {
        return {
            stateOptions: BRAZILIAN_STATES,
            citySelectConfig: {
                route: 'v2.core.states.cities',
                searchField: 'city',
                filters: {},
            },
        }
    },

    computed: {
        errors() {
            return this.address?.errors || { list: () => [], remove: () => {}, has: () => false, set: () => {} };
        }
    },

    created() {
        if (this.address?.state) {
            const normalizedState = this.normalizeState(this.address.state);
            if (this.address.state !== normalizedState) {
                 this.address.state = normalizedState;
            }
            this.updateCityFilter(normalizedState, this.address.city);
        }
    },

    watch: {
        'address.state'(newState) {
            if (!this.address) return;

            const normalized = this.normalizeState(newState);

            if (normalized !== newState) {
                this.address.state = normalized;
                return;
            }

            this.updateCityFilter(normalized, this.address.city);
        }
    },

    methods: {
        async searchZipCode() {
            if (!this.address) return;

            this.errors.remove('zip_code');
            this.clearAddress(false);

            const cleanZipCode = this.address.zip_code ? this.address.zip_code.replace(/[^\d]/g, '') : '';

            if (cleanZipCode.length !== 8) {
                this.updateCityFilter(null);
                return;
            }

            try {
                const res = await axios.get(this.route('v2.core.zip_code', cleanZipCode));

                if (res.data) {
                    const { street, neighborhood, state, city } = res.data;
                    const normalizedState = this.normalizeState(state);

                    this.address.street = street;
                    this.address.district = neighborhood;
                    this.address.state = normalizedState;
                    this.address.city = city;
                    this.address.country = 'Brasil';

                    this.updateCityFilter(normalizedState, city);

                    this.$nextTick(() => {
                         if (this.$refs.addressNumber) {
                            this.$refs.addressNumber.focus();
                        }
                    });
                }
            } catch (error) {
                const statusCode = this.errorFeedback(error);
                if (statusCode === 422 && this.address && this.address.errors && error.response.data) {
                    this.address.errors.set(error.response.data);
                }
                this.clearAddress(true);
                this.updateCityFilter(null);
            }
        },

        clearAddress(clearZipCode = false) {
            if (clearZipCode) {
                this.address.zip_code = null;
            }
            this.address.street = null;
            this.address.number = null;
            this.address.district = null;
            this.address.city = null;
            this.address.state = null;
            this.address.country = null;
            this.address.complement = null;
        },

        handleStateChange(value) {
            const normalized = this.normalizeState(value);

            if (!normalized) {
                this.address.city = null;
            }
        },

        updateCityFilter(state, city = null) {
            const normalized = this.normalizeState(state);

            const filters = normalized ? { state: normalized } : {};

            if (city) {
                filters.city = city
            }

            this.citySelectConfig = {
                ...this.citySelectConfig,
                filters,
            };
        },

        normalizeState(value) {
            if (!value) {
                return '';
            }
            const formatted = String(value).trim().toUpperCase();
            return formatted.slice(0, 2);
        }
    }
}
</script>

<style>

</style>